import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import Icon from '../../components/AppIcon';
import CreateAudienceModal from './components/CreateAudienceModal';
import api from '../../api/axios';

const SOURCE_BADGE_STYLES = {
  'csv_upload': 'bg-blue-50 text-blue-700 border border-blue-100',
  'CSV Upload': 'bg-blue-50 text-blue-700 border border-blue-100',
  'shopify': 'bg-green-50 text-green-700 border border-green-100',
  'Shopify': 'bg-green-50 text-green-700 border border-green-100',
  'api': 'bg-orange-50 text-orange-700 border border-orange-100',
  'API': 'bg-orange-50 text-orange-700 border border-orange-100',
  'crm': 'bg-violet-50 text-violet-700 border border-violet-100',
  'CRM': 'bg-violet-50 text-violet-700 border border-violet-100',
  'manual': 'bg-yellow-50 text-yellow-700 border border-yellow-100',
  'Manual Entry': 'bg-yellow-50 text-yellow-700 border border-yellow-100',
};

const SOURCE_DISPLAY = {
  'csv_upload': 'CSV Upload',
  'shopify': 'Shopify',
  'api': 'API',
  'crm': 'CRM',
  'manual': 'Manual Entry',
};

const formatContacts = (n) => n?.toLocaleString('en-IN');

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const UsedInBadge = ({ count }) => {
  if (!count || count === 0) return <span className="text-gray-400 text-sm">Not Used</span>;
  return (
    <span className="text-gray-700 text-sm font-medium">
      {count} {count === 1 ? 'Campaign' : 'Campaigns'}
    </span>
  );
};

const SourceBadge = ({ source }) => {
  const style = SOURCE_BADGE_STYLES?.[source] || 'bg-gray-100 text-gray-600 border border-gray-200';
  const display = SOURCE_DISPLAY?.[source] || source;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style}`}>
      {display}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    ready: 'bg-green-50 text-green-700 border-green-100',
    processing: 'bg-blue-50 text-blue-700 border-blue-100',
    pending_processing: 'bg-yellow-50 text-yellow-700 border-yellow-100',
    failed: 'bg-red-50 text-red-700 border-red-100',
  };
  const labels = {
    ready: 'Ready',
    processing: 'Processing',
    pending_processing: 'Pending',
    failed: 'Failed',
  };
  const style = styles?.[status] || 'bg-gray-100 text-gray-600 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${style}`}>
      {labels?.[status] || status}
    </span>
  );
};

const AudiencePage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalElements, setTotalElements] = useState(0);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'single'|'bulk', id?: number }

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3500);
  };

  const fetchAudiences = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/api/v1/audience', {
        params: { page: currentPage - 1, size: pageSize },
      });
      setAudiences(data?.data || []);
      setTotalElements(data?.pagination?.total_elements || data?.data?.length || 0);
    } catch (err) {
      console.error('Failed to fetch audiences:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchAudiences();
  }, [fetchAudiences]);

  // Poll for processing audiences
  useEffect(() => {
    const hasProcessing = audiences?.some(
      (a) => a?.status === 'processing' || a?.status === 'pending_processing'
    );
    if (!hasProcessing) return;

    const interval = setInterval(fetchAudiences, 5000);
    return () => clearInterval(interval);
  }, [audiences, fetchAudiences]);

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    setCurrentPage(1);
    fetchAudiences();
    showToast('Audience created. CSV processing started.');
  };

  const handleDelete = (id) => {
    setDeleteConfirm({ type: 'single', id });
  };

  const handleBulkDeleteClick = () => {
    if (selectedRows.length === 0) return;
    setDeleteConfirm({ type: 'bulk' });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      if (deleteConfirm.type === 'single') {
        await api.delete(`/api/v1/audience/${deleteConfirm.id}`);
        showToast('Audience deleted.');
      } else {
        await api.delete('/api/v1/audience/bulk', { data: { ids: selectedRows } });
        showToast(`${selectedRows.length} audience(s) deleted.`);
        setSelectedRows([]);
      }
      fetchAudiences();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to delete audience.';
      showToast(msg);
    } finally {
      setDeleteConfirm(null);
    }
  };

  const filtered = audiences?.filter((a) => {
    const matchSearch = a?.name?.toLowerCase()?.includes(search?.toLowerCase());
    const matchSource = sourceFilter ? a?.source === sourceFilter : true;
    return matchSearch && matchSource;
  });

  const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

  const allSelected = filtered?.length > 0 && filtered?.every((r) => selectedRows?.includes(r?.id));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedRows(selectedRows?.filter((id) => !filtered?.find((r) => r?.id === id)));
    } else {
      setSelectedRows([...new Set([...selectedRows, ...filtered.map((r) => r.id)])]);
    }
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) => prev?.includes(id) ? prev?.filter((x) => x !== id) : [...prev, id]);
  };

  const uniqueSources = [...new Set(audiences.map((a) => a.source))];

  return (
    <>
      <PrimaryNavigation />
      <div className="main-content min-h-screen bg-background flex flex-col">
        <div className="px-4 md:px-6 py-6 md:py-8 flex-1">

          {/* Page Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Audience</h1>
              <p className="text-sm text-gray-500 mt-1">Manage all customer lists available for use in AI calling campaigns.</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2F55FF] text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Icon name="Plus" size={16} />
              Create Audience
            </button>
          </div>

          {/* Search & Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search Audience"
                value={search}
                onChange={(e) => { setSearch(e?.target?.value); setCurrentPage(1); }}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 placeholder-gray-400"
              />
            </div>

            <select
              value={sourceFilter}
              onChange={(e) => { setSourceFilter(e?.target?.value); setCurrentPage(1); }}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 cursor-pointer"
            >
              <option value="">Source</option>
              {uniqueSources?.map((s) => <option key={s} value={s}>{SOURCE_DISPLAY?.[s] || s}</option>)}
            </select>

            {selectedRows?.length > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm text-gray-500">{selectedRows?.length} selected</span>
                <button
                  onClick={handleBulkDeleteClick}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <Icon name="Trash2" size={14} />
                  Delete
                </button>
              </div>
            )}
          </div>

          {/* Table Card */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-500">
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                  <span className="text-sm">Loading audiences...</span>
                </div>
              </div>
            ) : filtered?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
                  <Icon name="Users" size={28} className="text-blue-400" />
                </div>
                <h3 className="text-base font-semibold text-gray-800 mb-1">No Audience Lists Yet</h3>
                <p className="text-sm text-gray-500 max-w-xs mb-6">
                  Create or import your first audience to start running AI voice campaigns.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2F55FF] text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors"
                  >
                    <Icon name="Plus" size={15} />
                    Create Audience
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/60">
                        <th className="w-10 px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Audience Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Contacts</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Date Created</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Source</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Used In</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filtered?.map((row) => (
                        <tr
                          key={row?.id}
                          onClick={() => navigate(`/audience/${row?.id}`)}
                          className={`group transition-colors hover:bg-gray-50/70 cursor-pointer ${selectedRows?.includes(row?.id) ? 'bg-blue-50/40' : ''}`}
                        >
                          <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedRows?.includes(row?.id)}
                              onChange={() => toggleRow(row?.id)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="font-semibold text-gray-900">{row?.name}</div>
                            {(row?.status === 'processing' || row?.status === 'pending_processing') && (
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden max-w-[120px]">
                                  <div
                                    className="h-full bg-blue-500 rounded-full transition-all"
                                    style={{ width: `${row?.totalRows > 0 ? Math.round((row?.processedRows / row?.totalRows) * 100) : 0}%` }}
                                  />
                                </div>
                                <span className="text-[11px] text-gray-400">
                                  {row?.processedRows || 0}/{row?.totalRows || '?'}
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3.5">
                            <StatusBadge status={row?.status} />
                          </td>
                          <td className="px-4 py-3.5 text-gray-700 font-medium tabular-nums">
                            {formatContacts(row?.validCount || 0)}
                          </td>
                          <td className="px-4 py-3.5 text-gray-500 whitespace-nowrap">{formatDate(row?.createdAt)}</td>
                          <td className="px-4 py-3.5">
                            <SourceBadge source={row?.source} />
                          </td>
                          <td className="px-4 py-3.5">
                            <UsedInBadge count={row?.usedInCampaigns} />
                          </td>
                          <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                title="Delete"
                                onClick={() => handleDelete(row?.id)}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <Icon name="Trash2" size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/40">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>Rows per page:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e?.target?.value)); setCurrentPage(1); }}
                      className="px-2 py-1 border border-gray-200 rounded-lg bg-white text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <span className="mr-2">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <Icon name="ChevronLeft" size={16} />
                    </button>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <Icon name="ChevronRight" size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Create Audience Modal */}
      {showCreateModal && (
        <CreateAudienceModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 animate-fade-in">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-4">
                <Icon name="Trash2" size={22} className="text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Delete Audience</h3>
              <p className="text-sm text-gray-500 mb-6">
                {deleteConfirm.type === 'single'
                  ? 'Are you sure you want to delete this audience? This action cannot be undone.'
                  : `Are you sure you want to delete ${selectedRows.length} selected audience(s)? This action cannot be undone.`}
              </p>
              <div className="flex items-center gap-3 w-full">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2.5 px-5 py-3 bg-gray-900 text-white text-sm font-medium rounded-xl shadow-xl animate-fade-in">
          <Icon name="CheckCircle" size={16} className="text-green-400 flex-shrink-0" />
          {toast}
        </div>
      )}
    </>
  );
};

export default AudiencePage;
